import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React from 'react'
import { MoveFocusInside } from 'react-focus-on'

import { UnstyledButton } from '../../components/UnstyledButton'
import { joinOptionalStringsToSpaceSeparatedString } from '../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars, SemanticColorToken } from '../themes'
import { Accordion, AccordionPhase } from './Accordion'
import type { Icon16 } from './FoundationIcon/icons/index'
import { Modal } from './Modal'
import {
  ROW_MARGIN_BORDERED,
  ROW_TRANSITION_DURATION,
  ROW_TRANSITION_TIMING_FUNCTION,
} from './Rows/Constants'
import { RowUtils } from './Rows/RowUtils'
import { Type } from './Type'
import { useUniqueId } from './useUniqueId'

const styles = {
  expandableSectionContainer: css({
    padding: `calc(2 * ${CssVars.size.gutterWidth}) 0`,
  }),
  header: css({
    // NOTE(omer.demirkan): To make the hoverable padded area
    // clickable. Also to add padding to focus ring.
    margin: `0 calc(-2 * ${CssVars.size.gutterWidth})`,
    padding: `0 calc(2 * ${CssVars.size.gutterWidth})`,
  }),
}

type Props = {
  /**
   * The name of the setting.
   */
  title: string
  /**
   * State that if the SettingsRow is currently editable.
   */
  isOpen: boolean
  /**
   * Callback responsible for toggling isOpen state.
   */
  onToggleOpen?: (newIsOpen?: boolean) => any
  /**
   * The current value of the setting.
   */
  value: string | number
  /**
   * Trailing text used to describe queues on the row itself.
   */
  metadata?: string
  /**
   * The content responsible for allowing the user to update
   * settings.
   *
   * Can be a render prop that accepts a ref for element to
   * be focused on when opened.
   */
  children?:
    | React.ReactNode
    | ((options: {
        autoFocusRef: React.Ref<HTMLInputElement>
      }) => React.ReactNode)
  /**
   * Makes the web row non-interactive.
   */
  isDisabled?: boolean
  overrideRootStyles?: CSSInterpolation
  /**
   * Determines if/how content is shown.
   */
  variant?: 'expandable' | 'modal' | 'readonly'
  /**
   * Optional icon in trailing items.
   */
  endIcon?: Icon16
  'aria-describedby'?: React.HTMLAttributes<HTMLDivElement>['aria-describedby']
  'aria-labelledby'?: React.HTMLAttributes<HTMLDivElement>['aria-labelledby']
  'aria-label'?: React.HTMLAttributes<HTMLDivElement>['aria-label']
}

/**
 * An Row for showing and updating settings. Provides different
 * surfaces to collect user input: expandable and modal.
 */
export function SettingsRow({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  children,
  endIcon,
  isDisabled = false,
  isOpen = false,
  metadata,
  onToggleOpen,
  overrideRootStyles,
  title,
  value,
  variant = 'expandable',
}: Props) {
  const buttonRef = React.useRef<HTMLButtonElement>(null)
  const autoFocusRef = React.useRef<HTMLInputElement>(null)

  // NOTE(omer.demirkan): Both state and ref.
  // State because we want to flag for a rerender when the transitioning
  // (opening, closing) animation is complete. Ref because <MoveFocusInside>
  // behaves strangely if we enable it even one render before its fully open.
  // Removing this will cause a strange padding bug when opening.
  // useLayoutEffect doesn't solve this since it fires after DOM mutations.
  const [isTransitioning, setIsTransitioning] = React.useState(false)
  const isTransitioningRef = React.useRef(false)
  const prevIsOpenRef = React.useRef(isOpen)
  if (prevIsOpenRef.current !== isOpen && variant === 'expandable') {
    isTransitioningRef.current = true
  }
  prevIsOpenRef.current = isOpen
  const handleAccordionStateChange = React.useCallback(
    (_: boolean, phase: AccordionPhase) => {
      setIsTransitioning(phase === AccordionPhase.Animate)
      isTransitioningRef.current = phase === AccordionPhase.Animate
    },
    []
  )

  React.useEffect(() => {
    if (isOpen && !isTransitioning) autoFocusRef.current?.focus()
  }, [isOpen, isTransitioning])

  const isReadOnly = variant === 'readonly'
  const handleToggleOpenClicked = React.useCallback(
    (event?: React.MouseEvent) => {
      if (isReadOnly || isDisabled) return

      onToggleOpen?.(!isOpen)

      // NOTE(omer.demirkan): Since both header and edit button
      // have this listener, so that one click on the edit button
      // doesn't trigger two listeners.
      if (event) event.stopPropagation()
    },
    [isReadOnly, isDisabled, onToggleOpen, isOpen]
  )
  const handleClose = React.useCallback(() => {
    onToggleOpen?.(false)
  }, [onToggleOpen])

  const { colors } = RowUtils.useRowColors(isDisabled)

  const isClosing = isTransitioningRef.current && !isOpen
  const isOpening = isTransitioningRef.current && isOpen

  const instanceId = useUniqueId()
  const titleId = `settings-row-${instanceId}-title`
  const valueId = `settings-row-${instanceId}-value`
  const metadataId = `settings-row-${instanceId}-metadata`
  const descriptiveIds = joinOptionalStringsToSpaceSeparatedString(
    ariaDescribedBy,
    titleId,
    valueId,
    metadata && metadataId
  )
  const contentId = useUniqueId()

  return (
    <>
      <div
        aria-describedby={descriptiveIds}
        aria-disabled={isDisabled}
        // TODO: When i18n comes, update this.
        aria-label={ariaLabel || `${isReadOnly ? 'See' : 'Update'} ${title}`}
        aria-labelledby={ariaLabelledBy}
        css={[
          {
            '&:first-of-type': {
              marginTop: '0',
            },
            margin:
              variant === 'expandable' && isOpen
                ? `${ROW_MARGIN_BORDERED} 0`
                : '0',
            transition: `margin ${ROW_TRANSITION_DURATION} ${ROW_TRANSITION_TIMING_FUNCTION}`,
          },
          overrideRootStyles,
        ]}
      >
        <RowUtils.Container
          isDisabled={isDisabled}
          isElevated={
            !isReadOnly && !isDisabled && variant === 'expandable' && isOpen
          }
          isInteractive={
            !isReadOnly &&
            !isDisabled &&
            (variant !== 'expandable' ||
              // NOTE(omer.demirkan): After isOpen turns false, the animation still
              // takes time. Having hover backgrounds while it's closing isn't what
              // design wants.
              (!isOpen && !isClosing))
          }
        >
          <div
            css={[
              styles.header,
              !isDisabled && !isReadOnly && { cursor: 'pointer' },
            ]}
            onClick={handleToggleOpenClicked}
          >
            <RowUtils.Section
              divider="bottom"
              mainContent={
                <>
                  <span id={titleId}>
                    <Type.Text
                      color={colors.secondaryText}
                      element="h6"
                      maxLines={3}
                      size="S"
                    >
                      {title}
                    </Type.Text>
                  </span>
                  <span id={valueId}>
                    <Type.Text
                      color={colors.primaryText}
                      element="p"
                      maxLines={3}
                      size="M"
                    >
                      {value}
                    </Type.Text>
                  </span>
                </>
              }
              onClick={handleToggleOpenClicked}
              trailingContent={
                <>
                  {metadata && (
                    <div id={metadataId}>
                      <Type.Text
                        color={colors.primaryText}
                        maxLines={3}
                        size="M"
                      >
                        {metadata}
                      </Type.Text>
                    </div>
                  )}
                  {endIcon && (
                    <RowUtils.Icon color={colors.icon} type={endIcon} />
                  )}
                  {!isReadOnly && (
                    // NOTE(omer.demirkan): extra since button doesn't
                    // respond to child styles on the container.
                    <div>
                      <UnstyledButton
                        ref={buttonRef}
                        aria-controls={contentId}
                        aria-describedby={descriptiveIds}
                        aria-disabled={isDisabled}
                        aria-expanded={isOpen}
                        // TODO: translate this when react-intl is available
                        aria-label={
                          !isReadOnly
                            ? `${isOpen ? 'Close' : 'Edit'} ${title}`
                            : `${title} is ${value}`
                        }
                        aria-owns={contentId}
                        onClick={handleToggleOpenClicked}
                        type="button"
                      >
                        <RowUtils.Icon color={colors.caret} type="Edit16" />
                      </UnstyledButton>
                    </div>
                  )}
                </>
              }
            />
          </div>

          {variant === 'expandable' && (
            <Accordion
              isOpen={isOpen}
              onStateChange={handleAccordionStateChange}
              overrideRootStyles={{
                transition: `height ${ROW_TRANSITION_DURATION} ${ROW_TRANSITION_TIMING_FUNCTION}`,
              }}
            >
              <MoveFocusInside
                disabled={!isOpen || isOpening || !!autoFocusRef.current}
              >
                <div
                  // NOTE(omer.demirkan): This is following w3's accordion (with inputs) example.
                  // https://www.w3.org/TR/wai-aria-practices-1.1/examples/accordion/accordion.html
                  aria-labelledby={titleId}
                  css={styles.expandableSectionContainer}
                  id={contentId}
                  role="region"
                >
                  {typeof children === 'function'
                    ? children({ autoFocusRef })
                    : children}
                </div>
              </MoveFocusInside>
            </Accordion>
          )}
        </RowUtils.Container>
      </div>
      {variant === 'modal' && isOpen && (
        <Modal
          foregroundColor={SemanticColorToken.Success}
          onClose={handleClose}
          width="standard"
        >
          <div id={contentId}>
            {typeof children === 'function'
              ? children({ autoFocusRef })
              : children}
          </div>
        </Modal>
      )}
    </>
  )
}
